/**
 * Quotation Item Card Component
 *
 * Card-based layout for quotation items on mobile devices
 *
 * @module pages/quotations/QuotationItemCard
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Card, Form, Button, InputGroup, Row, Col } from 'react-bootstrap';
import { ITEM_TYPE, UNITS } from '../../utils/constants';
import { formatCurrency } from '../../utils/formatters';
import { calculateItemTotal } from '../../utils/calculations';
import PriceSourceBadge from './PriceSourceBadge';

/**
 * QuotationItemCard component
 * @param {Object} props
 * @param {Object} props.item - Item data
 * @param {number} props.index - Item index
 * @param {Function} props.onUpdate - Callback when item is updated
 * @param {Function} props.onRemove - Callback when item is removed
 * @param {Function} props.onDuplicate - Callback when item is duplicated
 * @param {boolean} props.disabled - Disable editing
 */
const QuotationItemCard = ({
  item,
  index,
  onUpdate,
  onRemove,
  onDuplicate,
  disabled = false,
}) => {
  const itemTotal = calculateItemTotal(item.quantity || 0, item.unit_price || 0);

  /**
   * Handle field change
   */
  const handleChange = (field, value) => {
    onUpdate(index, { ...item, [field]: value });
  };

  return (
    <Card className="mb-3 quotation-item-card">
      <Card.Body className="p-3">
        {/* Header with item number and actions */}
        <div className="d-flex justify-content-between align-items-center mb-3">
          <span className="badge bg-light text-dark">
            Item #{index + 1}
          </span>
          <div className="d-flex gap-2">
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => onDuplicate(index)}
              disabled={disabled}
              title="Duplicar"
            >
              <i className="bi bi-files"></i>
            </Button>
            <Button
              variant="outline-danger"
              size="sm"
              onClick={() => onRemove(index)}
              disabled={disabled}
              title="Eliminar"
            >
              <i className="bi bi-trash"></i>
            </Button>
          </div>
        </div>

        {/* Type selection */}
        <Form.Group className="mb-3">
          <Form.Label className="small text-muted mb-1">Tipo</Form.Label>
          <Form.Select
            size="sm"
            value={item.item_type || ''}
            onChange={(e) => handleChange('item_type', e.target.value)}
            disabled={disabled}
          >
            <option value="">Seleccionar tipo...</option>
            <option value={ITEM_TYPE.MATERIAL}>Material</option>
            <option value={ITEM_TYPE.TOOL}>Herramienta</option>
            <option value={ITEM_TYPE.LABOR}>Mano de Obra</option>
            <option value={ITEM_TYPE.ADMIN}>Administración</option>
            <option value={ITEM_TYPE.OTHER}>Otro</option>
          </Form.Select>
        </Form.Group>

        {/* Description */}
        <Form.Group className="mb-3">
          <Form.Label className="small text-muted mb-1">Descripción</Form.Label>
          <Form.Control
            size="sm"
            type="text"
            value={item.description || ''}
            onChange={(e) => handleChange('description', e.target.value)}
            placeholder="Descripción del artículo..."
            disabled={disabled}
          />
        </Form.Group>

        {/* Quantity and Unit */}
        <Row className="mb-3">
          <Col xs={6}>
            <Form.Group>
              <Form.Label className="small text-muted mb-1">Cantidad</Form.Label>
              {/* `check_quantity_positive` = `quantity > 0` (estricto): el
                  mínimo real es 0.01, no 0 (#A7). */}
              <Form.Control
                size="sm"
                type="number"
                min="0.01"
                step="0.01"
                value={item.quantity || ''}
                onChange={(e) => handleChange('quantity', parseFloat(e.target.value) || 0)}
                disabled={disabled}
              />
            </Form.Group>
          </Col>
          <Col xs={6}>
            <Form.Group>
              <Form.Label className="small text-muted mb-1">Unidad</Form.Label>
              <Form.Select
                size="sm"
                value={item.unit || ''}
                onChange={(e) => handleChange('unit', e.target.value)}
                disabled={disabled}
              >
                <option value="">Unidad...</option>
                {UNITS.map((unit) => (
                  <option key={unit.value} value={unit.value}>
                    {unit.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>

        {/* Unit Price */}
        <Form.Group className="mb-3">
          <Form.Label className="small text-muted mb-1">Precio Unitario</Form.Label>
          {/* #617: sin `$`. `quotation_items` no tiene columna de moneda, y esta
              empresa cotiza en colones — el simbolo decia lo contrario. */}
          <InputGroup size="sm">
            <Form.Control
              type="number"
              min="0"
              step="0.01"
              value={item.unit_price || ''}
              onChange={(e) => handleChange('unit_price', parseFloat(e.target.value) || 0)}
              disabled={disabled}
            />
          </InputGroup>
          {/* #609 — de dónde salió este número. NULL no se pinta. */}
          <PriceSourceBadge priceSource={item.price_source} index={index} view="card" />
        </Form.Group>

        {/* Total */}
        <div className="d-flex justify-content-between align-items-center pt-2 border-top">
          <span className="text-muted">Total:</span>
          <strong className="text-primary fs-5">{formatCurrency(itemTotal)}</strong>
        </div>
      </Card.Body>
    </Card>
  );
};

QuotationItemCard.propTypes = {
  item: PropTypes.shape({
    item_type: PropTypes.string,
    description: PropTypes.string,
    quantity: PropTypes.number,
    unit: PropTypes.string,
    unit_price: PropTypes.number,
    // #609 — ver QuotationItemRow.
    price_source: PropTypes.string,
  }).isRequired,
  index: PropTypes.number.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  onDuplicate: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

export default QuotationItemCard;
